import { registrationChannelSchema, type RegistrationChannel } from '@houzy/schemas/partner-cockpit/onboarding';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { onboardingApplicationQueryOptions, useSaveRegistrationChannelMutation } from '../api/onboardingApi';
import { OnboardingFooter } from '../components/OnboardingFooter';
import { OnboardingStepContent } from '../components/OnboardingStepContent';
import { OnboardingStepError } from '../components/OnboardingStepError';
import { OnboardingStepHeading } from '../components/OnboardingStepHeading';
import { useOnboardingState } from '../state/onboardingContext';
import { getNextStep } from '../steps';

const CHANNEL_KEYS: Record<RegistrationChannel, string> = {
    OnMyOwn: 'ON_MY_OWN',
    AssistedByPhone: 'ASSISTED_BY_PHONE',
    SalesMeeting: 'SALES_MEETING',
};

export function RegistrationChannelStep(): ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: application } = useQuery(onboardingApplicationQueryOptions);
    const saveRegistrationChannel = useSaveRegistrationChannelMutation();
    const { registrationChannel, setRegistrationChannel } = useOnboardingState();

    const prefilled = useRef(false);
    const savedChannel = application?.registrationChannel;
    useEffect(() => {
        if (prefilled.current || !savedChannel) {
            return;
        }
        prefilled.current = true;
        setRegistrationChannel((prev) => (prev.channel ? prev : { channel: savedChannel }));
    }, [savedChannel, setRegistrationChannel]);

    const persistAndContinue = (answer: RegistrationChannel | undefined): void => {
        if (saveRegistrationChannel.isPending) {
            return;
        }
        saveRegistrationChannel.mutate(
            { channel: answer },
            {
                onSuccess: () => {
                    const next = getNextStep('registration-channel');
                    if (next) {
                        void navigate({ to: next.path });
                    }
                },
            },
        );
    };

    const handleChange = (value: string): void => {
        const parsed = registrationChannelSchema.safeParse(value);
        if (parsed.success) {
            setRegistrationChannel({ channel: parsed.data });
        }
    };

    return (
        <>
            <OnboardingStepContent>
                <Stack spacing={6}>
                    <OnboardingStepHeading
                        title={t('ONBOARDING.REGISTRATION_CHANNEL.TITLE')}
                        description={t('ONBOARDING.REGISTRATION_CHANNEL.DESCRIPTION')}
                    />

                    <OnboardingStepError error={saveRegistrationChannel.error} />

                    <RadioGroup
                        aria-label={t('ONBOARDING.REGISTRATION_CHANNEL.TITLE')}
                        value={registrationChannel.channel ?? ''}
                        onChange={(event) => handleChange(event.target.value)}
                        sx={{ gap: 0.5 }}>
                        {registrationChannelSchema.options.map((option) => (
                            <FormControlLabel
                                key={option}
                                value={option}
                                control={<Radio />}
                                label={
                                    <Stack spacing={0.5} sx={{ pt: 1.25 }}>
                                        <Typography variant="subtitle2" component="span">
                                            {t(`ONBOARDING.REGISTRATION_CHANNEL.${CHANNEL_KEYS[option]}.LABEL`)}
                                        </Typography>
                                        <Typography variant="caption" component="span" color="text.secondary">
                                            {t(`ONBOARDING.REGISTRATION_CHANNEL.${CHANNEL_KEYS[option]}.DESCRIPTION`)}
                                        </Typography>
                                    </Stack>
                                }
                                sx={{
                                    m: 0,
                                    p: 1.5,
                                    gap: 1,
                                    alignItems: 'flex-start',
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    bgcolor: 'background.paper',
                                }}
                            />
                        ))}
                    </RadioGroup>
                </Stack>
            </OnboardingStepContent>

            <OnboardingFooter
                currentStep="registration-channel"
                nextDisabled={registrationChannel.channel === undefined || saveRegistrationChannel.isPending}
                onNext={() => persistAndContinue(registrationChannel.channel)}
                onSkip={() => persistAndContinue(undefined)}
            />
        </>
    );
}
